import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { FaStar } from 'react-icons/fa'
import { s3Static } from '../../../../utils/constants'
import { Breadcrumb } from '../../../commons/Breadcrumb'
import { Faq } from '../../../commons/Faq'
import { buildBreadcrumb } from '../../../../utils/breadcrumb'
import { buildServiceHubGraph } from '../../../../utils/service-hub-schema'
import {
    categorySlug,
    findItems,
    formatPrice,
    fromPrice,
    groupByCategory,
    priceRange,
} from '../../../../utils/handyman-price-data'
import { SITE_URL } from '../../../../utils/sitemap-config'
import { googleRating, googleReviewCount } from '../../../../utils/business-info'
import { handymanMarketRatesVerifiedOn, marketCallOutFeeMax } from '../../../../data/handyman-market-rates'
import { HandymanBookingPanel } from './HandymanBookingPanel'
import { PriceList } from '@/commons/service-hub/PriceList'
import { RelatedLinks } from '@/commons/service-hub/RelatedLinks'

const formatReviewed = (iso) =>
    new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Singapore' })

/**
 * Shared body for the two price-list pages:
 *   /sg/handyman-singapore-services/handyman-price-list
 *   /sg/electrician-services-singapore/electrician-price-singapore
 *
 * Every amount on the page comes from `items`, the live price list, or from
 * data/handyman-market-rates.js. The page itself contains no hard-coded prices.
 */
export const HandymanPricePage = ({
    path,
    label,               // "Handyman" | "Electrician"
    title,
    description,
    h1,
    items,
    handymanPriceList,
    requestCategorySettings,
    marketRates,
    faqs,
    cheapTips,
    priceFactors,
    relatedLinks,
    lastReviewed,
    image,
    bookingProps = {},
}) => {
    const pageUrl = SITE_URL + path
    const heroImage = s3Static + image
    const range = priceRange(items)
    const groups = groupByCategory(items)
    const labelLower = label.toLowerCase()

    const schema = buildServiceHubGraph({
        pageUrl,
        pageName: title,
        description,
        trail: buildBreadcrumb(path, `${label} Price List`),
        items,
        serviceName: `${label} Services Singapore`,
        serviceType: `${label} Service`,
        image: heroImage,
        datePublished: '2026-09-23T08:00:00+08:00',
        modifiedDate: lastReviewed,
    })

    const toc = [
        { href: '#price-summary', label: 'Price summary' },
        { href: '#full-price-list', label: 'Full price list' },
        { href: '#market-prices', label: 'Typical market prices' },
        { href: '#price-factors', label: 'What affects the price' },
        { href: '#cheap', label: `How to get a cheap ${labelLower}` },
        { href: '#faq', label: 'FAQs' },
    ]

    const marketRows = marketRates.map((row) => ({
        ...row,
        ours: fromPrice(findItems(handymanPriceList, row.ourItems, `market rate "${row.job}"`)),
    }))

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={description} />
                <meta name="author" content="StringsSG" />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:image" content={heroImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={heroImage} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
            </Head>

            <Breadcrumb pageTitle={`${label} Price List`} jsonLd={false} />

            <header className="max-w-5xl mx-auto px-4 pb-6 space-y-3">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">{h1}</h1>
                <p className="text-xs text-gray-500">
                    Prices checked <time dateTime={lastReviewed}>{formatReviewed(lastReviewed)}</time>. Market comparison verified {formatReviewed(handymanMarketRatesVerifiedOn)}.
                </p>
                {range &&
                    <p className="text-gray-700 leading-relaxed">
                        <strong>Short answer:</strong> StringsSG {labelLower} jobs in Singapore cost from <strong>{formatPrice(range.low)}</strong> to{' '}
                        <strong>{formatPrice(range.high)}</strong> across {items.length} fixed-price jobs. You see the exact price before you
                        confirm, so there is no hourly billing.
                    </p>
                }
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="flex items-center gap-1 text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">
                        <FaStar className="text-amber-500" aria-hidden="true" />
                        {googleRating} from {googleReviewCount} Google reviews
                    </span>
                    <span className="text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">Upfront fixed pricing</span>
                    <span className="text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">Same-day slots</span>
                </div>
            </header>

            {/* Category summary cards double as the jump menu into the full list. */}
            <section id="price-summary" className="max-w-5xl mx-auto px-4 pb-6 scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{label} Price Summary</h2>
                <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {groups.map(({ category, items: rows }) => {
                        const r = priceRange(rows)
                        return (
                            <li key={category}>
                                <a
                                    href={`#prices-${categorySlug(category)}`}
                                    className="block h-full bg-white border border-gray-200 rounded-2xl p-3 hover:border-orange-300 transition"
                                >
                                    <span className="block text-xs font-bold uppercase tracking-wide text-orange-600">{category}</span>
                                    <span className="block text-sm font-bold text-gray-900 mt-1">
                                        {r.low === r.high ? formatPrice(r.low) : `${formatPrice(r.low)} to ${formatPrice(r.high)}`}
                                    </span>
                                    <span className="block text-xs text-gray-500 mt-0.5">{rows.length} {rows.length === 1 ? 'job' : 'jobs'}</span>
                                </a>
                            </li>
                        )
                    })}
                </ul>
            </section>

            <section aria-labelledby="book-heading" className="bg-linear-to-b from-orange-50 to-white border-y border-orange-100 py-8 px-3">
                <div className="max-w-3xl mx-auto text-center mb-4">
                    <h2 id="book-heading" className="text-xl md:text-2xl font-bold text-gray-900">Book a {label} at These Prices</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Tap a job to see its fixed price, then pick a date and time. It takes under a minute.
                    </p>
                </div>
                <HandymanBookingPanel
                    variant="light"
                    requestCategorySettings={requestCategorySettings}
                    handymanPriceList={handymanPriceList}
                    handymanType={label}
                    whatsAppText={`Hi, I'd like to book ${label === 'Electrician' ? 'an electrician' : 'a handyman'}.`}
                    {...bookingProps}
                />
            </section>

            <nav aria-label="On this page" className="max-w-5xl mx-auto px-4 py-6">
                <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-2">On this page</h2>
                <ul className="flex flex-wrap gap-2">
                    {toc.map(({ href, label: text }) => (
                        <li key={href}>
                            <a href={href} className="inline-flex items-center min-h-10 px-3 rounded-full bg-gray-100 text-sm text-gray-800 hover:bg-orange-50">
                                {text}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <PriceList
                id="full-price-list"
                items={items}
                title={`Full ${label} Price List`}
                subtitle="Fixed prices per job. Items marked supply and install include the part."
                showCategoryAnchors
            />

            <section id="market-prices" className="max-w-5xl mx-auto px-4 py-10 scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Typical {label} Prices in Singapore</h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    Starting prices published by other Singapore {labelLower} companies for the same jobs, labour only, next to the lowest StringsSG
                    price for that job. Other providers may add a transport or call-out fee on top.
                </p>
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                    <table className="w-full text-sm">
                        <caption className="sr-only">Typical Singapore {labelLower} prices compared with StringsSG</caption>
                        <thead className="bg-gray-50 text-left text-gray-600">
                            <tr>
                                <th scope="col" className="px-3 py-2.5 font-semibold">Job</th>
                                <th scope="col" className="px-3 py-2.5 font-semibold whitespace-nowrap">Typical SG price</th>
                                <th scope="col" className="px-3 py-2.5 font-semibold whitespace-nowrap">StringsSG</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {marketRows.map((row) => (
                                <tr key={row.job}>
                                    <th scope="row" className="px-3 py-2.5 font-medium text-gray-900 text-left">{row.job}</th>
                                    <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">${row.low} to ${row.high.toLocaleString('en-SG')}</td>
                                    <td className="px-3 py-2.5 font-bold text-green-700 whitespace-nowrap">
                                        {row.ours != null ? `from ${formatPrice(row.ours)}` : 'Ask us'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section id="price-factors" className="max-w-5xl mx-auto px-4 pb-10 scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What Affects {label} Prices</h2>
                <ul className="space-y-3">
                    {priceFactors.map(({ heading, text }) => (
                        <li key={heading} className="bg-white border border-gray-200 rounded-2xl p-4">
                            <h3 className="text-sm font-bold text-gray-900 mb-1">{heading}</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                        </li>
                    ))}
                </ul>
            </section>

            <section id="cheap" className="bg-orange-50 py-10 px-4 scroll-mt-20">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">How to Get a Cheap {label} in Singapore</h2>
                    <ol className="space-y-3 list-decimal pl-5 text-gray-700 leading-relaxed">
                        {cheapTips(marketCallOutFeeMax).map(({ heading, text }) => (
                            <li key={heading}>
                                <strong>{heading}.</strong> {text}
                            </li>
                        ))}
                    </ol>
                    <div className="pt-6">
                        <Link
                            href="#book"
                            className="inline-flex items-center justify-center min-h-12 px-6 rounded-full bg-linear-to-tr from-orange-600 to-amber-400 text-white font-bold"
                        >
                            Book at these prices
                        </Link>
                    </div>
                </div>
            </section>

            <section id="faq" className="max-w-5xl mx-auto scroll-mt-20">
                <Faq title={`${label} Price FAQs`} questionsAnswers={faqs} showSearch={false} />
            </section>

            <RelatedLinks title="Services on This Price List" links={relatedLinks} />
            {/* Room for the sticky Book / WhatsApp bar on mobile. */}
            <div aria-hidden="true" className="h-20 md:hidden" />
        </>
    )
}
